import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Channel,
  CommonsOffersCreateOfferForm,
  core_apimessages_NegotiationParty,
  core_apimessages_BumpKey,
  core_apimessages_Listing_ListingType,
  ListingCreateOfferButtonData,
  core_apimessages_UserActionName,
  CreateUserActionMeData,
  Item2AvailableActionsData,
  core_apimessages_UserActionStatus,
} from '../rql';
import SignupSigninModal from '../components/signup_signin_modal';
import { IUserContext, withUserContext } from '../components/user_context_provider';
import Modal from '../components/modal';
import OfferCreateForm from './offer_create_form';
import CreateOfferModal from './create_offer_modal';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import gql from 'graphql-tag';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { LISTING_STATES } from '../constants';
import { mParticleListingFields } from '../components/with_mparticle_listings';
import { withRouter, WithRouterProps } from 'react-router';
import { resolveCurrentView } from '../event_tracking/favorites';
import { withCreateUserActionMutation, CoreUserActionsCreateMutationFn, useCreateUserAction } from '../user_actions/create_action_mutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import { SellerOpenToOffersPopover } from './seller_open_to_offers_popover';
import { isExperimentEnabled } from '../user_context_helpers';

interface IExternalProps {
  listing: ListingCreateOfferButtonData.Fragment;
  isSeller?: boolean;
  onOfferSuccess?: Function;
  listingThumbnail?: string;
  disabled?: boolean;
  offerCount?: number;
  position?: number;
  parentComponentName?: string;
  bumpKey?: core_apimessages_BumpKey;
  userTotalOffersCount?: number;
  className?: string;
  availableActions?: Item2AvailableActionsData.Fragment[];
  me?: CreateUserActionMeData.Fragment;
}

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = IExternalProps & IUserContext & MutationProps & WithRouterProps;

export const ListingCreateOfferButtonFragment = gql`
  fragment ListingCreateOfferButtonData on Listing {
    id
    _id
    state
    listingType
    ...mParticleListingFields
  }
  ${mParticleListingFields}
`;

export const Item2LoggedInOffersCountFragment = gql`
  fragment Item2LoggedInOffersCountData on rql_Me {
    id
    profile {
      totalBuyerOffersCreated
    }
  }
`;

function getTrackedExperiments(props: IProps) {
  if (!props.user.loggedOut) {
    if (props.userTotalOffersCount === 0) {
      return [
        experiments.OPEN_TO_OFFERS_LOGGED_IN,
        experiments.OFFER_MODAL_DESIGN_UPDATES,
      ];
    } else {
      return [
        experiments.OFFER_MODAL_DESIGN_UPDATES,
      ];
    }
  }
  return [];
}

export function canAcceptOffers(listing: ListingCreateOfferButtonData.Fragment) {
  return listing.state === LISTING_STATES.LIVE &&
    listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER;
}

export function handleSuccess(negotiation: CommonsOffersCreateOfferForm.Negotiations, onOfferSuccess) {
  const otherParty =
    negotiation.lastOffer.initiatingParty === core_apimessages_NegotiationParty.BUYER ?
      I18n.t('commons.offers.seller') :
      I18n.t('commons.offers.buyer');

  const initialOfferMessage = I18n.t('commons.offers.initialSubmissionSuccess', {
    otherParty,
    amount: negotiation.lastOffer.prices.totalPrice.display.display,
  });

  if (onOfferSuccess) {
    onOfferSuccess(initialOfferMessage);
  }
}

export function availableOfferButtonAction(actions: Item2AvailableActionsData.Fragment[]) {
  return actions?.find(action => action.name === core_apimessages_UserActionName.SELLER_OPEN_TO_OFFERS_CALLOUT && action.status === core_apimessages_UserActionStatus.AVAILABLE);
}

function Button({ disabled, toggleModal }) {
  return (
    <button
      className="offer-action"
      data-disabled={disabled || null}
      onClick={toggleModal}
      type="button"
    >
      {I18n.t('commons.offers.action.create')}
    </button>
  );
}

function OfferSignupCallout() {
  return (
    <div className="mb-2">
      <RCAlertBox type="success">
        <h4 className="size-110 weight-bold mb-1">
          {I18n.t('commons.offers.benefits.title')}
        </h4>
        <ul className="fa-ul">
          <li>
            <span className="fa fa-check fa-li color-green" />
            {I18n.t('commons.offers.benefits.saveMoney')}
          </li>
          <li>
            <span className="fa fa-check fa-li color-green" />
            {I18n.t('commons.offers.benefits.quickResponse')}
          </li>
        </ul>
      </RCAlertBox>
    </div>
  );
}

function SignupSigninModalOrOfferModal({ user, openDialog, handleClose, listing, isSeller, handleSuccess, bumpKey, inModalDialogExp }) {
  if (user.loggedOut) {
    return (
      <SignupSigninModal
        active={openDialog}
        onRequestClose={handleClose}
        trackSource="Offer"
        messageComponent={<OfferSignupCallout />}
      />
    );
  }

  if (inModalDialogExp) {
    return (
      <CreateOfferModal
        listingId={listing.id}
        isSeller={isSeller}
        onSuccess={handleSuccess}
        onCancel={handleClose}
        isDisplayed={openDialog}
        bumpKey={bumpKey}
      />
    );
  }

  return (
    <Modal
      onRequestClose={handleClose}
      isOpen={openDialog}
      headerTitle={I18n.t('commons.offers.action.create')}
      paddedBody
    >
      <OfferCreateForm
        listingId={listing.id}
        isSeller={isSeller}
        shopChannel={core_apimessages_Channel.REVERB}
        onSuccess={handleSuccess}
        onCancel={handleClose}
        isDisplayed={openDialog}
        bumpKey={bumpKey}
      />
    </Modal>
  );
}

export function CreateOfferButton(props: IProps) {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dismissedPopover, setDismissedPopover] = React.useState(false);

  const inModalDialogExp = isExperimentEnabled(props.user, experiments.OFFER_MODAL_DIALOG_CONVERSION);

  const createUserActionAvailable = props.me && availableOfferButtonAction(props.availableActions);
  const createUserActionState = useCreateUserAction(
    core_apimessages_UserActionName.SELLER_OPEN_TO_OFFERS_CALLOUT,
    props.me,
    {
      listingId: props.listing.id,
    },
    props.createUserAction,
  );

  React.useEffect(() => {
    if (openDialog) {
      const trackedExperiments = getTrackedExperiments(props);

      trackEvent({
        eventName: MParticleEventName.ClickedOfferButton,
        listingId: props.listing.id,
        listing: props.listing,
        position: props.position,
        componentName: props.parentComponentName,
        experiments: formatExperimentsForEventAttributes(props.user, trackedExperiments),
        currentView: resolveCurrentView(props.location),
        role: props.isSeller ? 'seller' : 'buyer',
      });
    }
  }, [openDialog]);

  function toggleModal(e) {
    // When handle closing the modal, it will blow up because there is no event being passed in
    // so we have to guard
    e.preventDefault();
    e.stopPropagation();
    setOpenDialog(!openDialog);
  }

  if (!canAcceptOffers(props.listing)) {
    return null;
  }

  return (
    <div className={props.className}>
      {createUserActionAvailable ? (
        <SellerOpenToOffersPopover
          isOpen={!dismissedPopover}
          onDismiss={() => {
            setDismissedPopover(true);
            createUserActionState.createUserAction();
          }}
          anchor={(
            <div>
              <Button
                disabled={props.disabled}
                toggleModal={toggleModal}
              />
            </div>
          )}
        />
      ) : <div>
        <Button
          disabled={props.disabled}
          toggleModal={toggleModal}
        />
      </div>}
      {!!props.offerCount &&
        <div
          data-offer-count
          className="size-80 opacity-70 mt-half align-center"
        >
          {I18n.t('commons.offers.offerCount', { count: props.offerCount })}
        </div>
      }
      <SignupSigninModalOrOfferModal
        user={props.user}
        openDialog={openDialog}
        handleClose={() => { setOpenDialog(false); }}
        listing={props.listing}
        isSeller={props.isSeller}
        handleSuccess={(negotiation) => {
          setOpenDialog(false);
          handleSuccess(negotiation, props.onOfferSuccess);
        }}
        bumpKey={props.bumpKey}
        inModalDialogExp={inModalDialogExp}
      />
    </div>
  );
}

export default connect<IExternalProps>([
  withCreateUserActionMutation,
  withRouter,
  withUserContext,
])(CreateOfferButton);
